import React, { useState, useContext, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import useSelection from "../hooks/useSelection";
import NtTouchableEffect from "./NtTouchableEffect";
import NtText from "./NtText";
import NtMaterialIcon from "./NtMaterialIcon";
import NtDevider from "./NtDevider";
import NtModalActiveButton from "./NtModalActiveButton";
import NtModalUnactiveButton from "./NtModalUnactiveButton";
import colors from "../config/colors";
import Logger from "../common/utils/Logger";
import UserContext from "../context/UserContext";
import moment from "moment-timezone";
import NtActivityIndicator from "./NtActivityIndicator";

function NtListHeaderExportModal({
  onCancel,
  onExported,
  selectedColumns,
  columns,
  overideColumnName = [],
  exportFetchDataAsync,
  exportDataParser,
  exportFileName,
}) {
  const [loading, setLoading] = useState(false);
  const selection = useSelection();
  const context = useContext(UserContext);

  const isRequiredFields = () => {
    return selection.selections?.length > 0;
  };

  useEffect(() => {
    //will set the defaults....
    let defaults = [];
    columns?.map((each) => {
      if (selectedColumns?.find((eachColumn) => each.id == eachColumn.id)) {
        defaults.push(each.id);
      }
    });
    selection.setSelections(defaults);
  }, []);

  const handleExport = async () => {
    setLoading(true);
    let selectedColumns = [];
    columns?.map((each) => {
      if (selection.isSelected(each.id)) {
        selectedColumns.push(each);
      }
    });

    const response = await exportFetchDataAsync();
    handleDownloadData(response, selectedColumns);

    setLoading(false);
    if (onExported) {
      onExported();
    }
  };

  const handleDownloadData = (data, selectedColumns) => {
    let csvData = "";

    //first... will put all the column names...
    selectedColumns.map((each) => {
      const override = overideColumnName?.find(
        (eachOverride) => eachOverride.id == each.id
      );
      csvData += `${override?.title || each.title},`;
    });
    csvData = csvData.substring(0, csvData.length - 1);

    //next... will start adding the data.....

    data?.map((item) => {
      let row = "";
      selectedColumns.map((eachColumn) => {
        const parsed = exportDataParser
          ? exportDataParser(item, eachColumn.id)
          : null;

        if (parsed) {
          row += `${parsed},`;
        } else {
          row += `${item[eachColumn.id]},`;
        }
      });
      row = row.substring(0, row.length - 1);
      csvData += `\n${row}`;
    });

    const blob = new Blob([csvData], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${exportFileName}-${
      context?.user?.environment?.code
    }-${moment().format("YYYYMMDD")}.csv`;
    link.href = url;
    link.click();
  };

  const handleDefault = () => {
    let defaults = [];
    columns?.map((each) => {
      if (each.default == true) {
        defaults.push(each.id);
      }
    });
    selection.setSelections(defaults);
  };

  const renderRow = (item, index) => {
    const override = overideColumnName?.find((each) => each.id == item.id);

    return (
      <NtTouchableEffect
        key={item.id}
        onPress={() => {
          if (selection.isSelected(item.id)) {
            selection.removeSelection(item.id);
          } else {
            selection.addSelection(item.id);
          }
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <NtText
            style={[
              styles.rowText,
              {
                flex: 1,
                color: selection.isSelected(item.id)
                  ? colors.darkest
                  : colors.lighGray,
              },
            ]}
          >
            {override?.title || item.title}
          </NtText>
          {selection.isSelected(item.id) && (
            <NtMaterialIcon name="check" size={20} color={colors.darkest} />
          )}
        </View>

        <NtDevider />
      </NtTouchableEffect>
    );
  };

  return (
    <View style={styles.container}>
      <NtText style={styles.title}>Export Data</NtText>
      <NtText style={[styles.subtitle, { marginTop: 6 }]}>
        Select the columns you wish to include in the export.
      </NtText>

      <ScrollView scrollEnabled={true} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={{ marginTop: 20, marginBottom: 20 }}>
          {columns?.map((each, index) => renderRow(each, index))}
        </View>
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 25,
        }}
      >
        <NtModalActiveButton
          containerStyle={{}}
          title={"Default"}
          onPress={() => {
            handleDefault();
          }}
          enabled={true}
        />

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={"Export Data"}
            onPress={() => {
              if (isRequiredFields()) {
                handleExport();
              }
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    maxHeight: 600,
    padding: 15,
  },
  title: {
    color: colors.darkest,
    fontSize: 15,
    fontWeight: "600",
  },
  subtitle: {
    color: colors.darker,
    fontSize: 14,
    fontWeight: "500",
  },
  cancelButton: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.darkest,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
  },
  addButton: {
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    backgroundColor: colors.blue,
  },
  uploadResponseText: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.darkest,
  },
  rowText: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.lighGray,
  },
});

export default NtListHeaderExportModal;
